import React, { useState, useEffect } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Divider,
} from "@mui/material";
import { ArrowBack, AccountCircle, ArrowDropDown } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useThemeMode } from "../../context/ThemeModeContext";
import useDeviceDetection from "../../utils/useDeviceDetection";
import { STORAGE_KEYS } from "../../utils/StorageKeys";
import { useUserContext } from "../../context/UserContext";

interface InnerTopbarProps {
    title: string;
}

const InnerTopBar: React.FC<InnerTopbarProps> = ({ title }) => {
    const navigate = useNavigate();
    const { isDarkMode, toggleDarkMode } = useThemeMode();
    const { isMobile } = useDeviceDetection();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [name, setName] = useState<string>("User");
    const { handleLogout } = useUserContext()

    useEffect(() => {
        const userData = localStorage.getItem(STORAGE_KEYS.USER);
        if (userData) {
            try {
                const parsed = JSON.parse(userData);
                setName(parsed.name || "User");
            } catch {
                setName("User");
            }
        }
    }, []);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <AppBar position="fixed" elevation={0} color="primary">
            <Toolbar sx={{
                gap: isMobile ? 1 : 3,
                alignItems: "center",
                px: isMobile ? 2 : 3,
                minHeight: isMobile ? "56px" : "64px",
                flexGrow: 1, display: 'flex',
            }}>
                {/* Back Button */}
                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="back"
                    onClick={() => navigate(-1)}
                    sx={{ mr: 1 }}
                >
                    <ArrowBack />
                </IconButton>
                {/* Dynamic Title */}
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 400 }}>
                    {title}
                </Typography>
                {/* Right side - Contact button or Login/Logout */}
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>

                    <IconButton
                        size={isMobile ? "medium" : "large"}
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMenuOpen}
                        sx={{
                            borderRadius: 2,
                            px: isMobile ? 1 : 2,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                            },
                        }}
                    >
                        <AccountCircle sx={{ mr: isMobile ? 0.5 : 1 }} />
                        {!isMobile && (
                            <Typography variant="body1" sx={{ textTransform: "none", fontWeight: 500 }}>
                                {name}
                            </Typography>
                        )}
                        <ArrowDropDown />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        <MenuItem onClick={() => { handleMenuClose(); navigate("/reset-password"); }}>
                            Reset Password
                        </MenuItem>
                        <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>
                            Logout
                        </MenuItem>
                    </Menu>
                    {/* Theme toggle switch */}
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: "rgba(255,255,255,.4)" }} />
                    <IconButton
                        color="inherit"
                        aria-label="toggle theme"
                        onClick={toggleDarkMode}
                        sx={{ ml: 1 }}
                    >
                        {isDarkMode ? (
                            <span role="img" aria-label="dark mode">🌙</span>
                        ) : (
                            <span role="img" aria-label="light mode">☀️</span>
                        )}
                    </IconButton>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default InnerTopBar;